<template>
  <div class="public-questions-sidebar">
    <div class="sidebar-header">
      <div class="header-top">
        <img class="logo" src="@/assets/isologotipo_chatpts_transparente.svg" alt="Logo de ChatPTS"
          @click="goToMainView" />
        <button v-if="isMobile" class="close-sidebar-button" @click="closeSidebar">
          <ion-icon name="close-outline"></ion-icon>
        </button>
      </div>
      <!-- <div class="platform-buttons" v-if="isAdmin"> -->
      <div class="platform-buttons">
        <button class="platform-button whatsapp-button" @click="goToWhatsApp">
          <ion-icon name="logo-whatsapp" />WA
        </button>
        <button class="platform-button telegram-button" @click="goToTelegram">
          <ion-icon name="send" />TG
        </button>
      </div>
      <h2>Preguntas Públicas</h2>
      <div class="sort-selector">
        <select v-model="sortBy" @change="loadPublicQuestions(true)">
          <option value="random">Aleatorias</option>
          <option value="recent">Más recientes</option>
        </select>
      </div>
    </div>

    <div class="questions-list" ref="questionsList" @scroll="handleScroll">
      <div v-if="loading && !publicQuestions.length" class="loading-indicator">
        Cargando...
      </div>

      <template v-else>
        <public-question-chip v-for="question in publicQuestions" :key="question._id" :question="question"
          :isActive="selectedQuestionId === question._id" @click="selectQuestion" />

        <div v-if="loading" class="loading-indicator">
          Cargando más...
        </div>

        <div v-if="!loading && !hasMoreQuestions" class="end-message">
          No hay más preguntas para mostrar
        </div>
      </template>
    </div>

    <div class="user-info" :class="{ 'admin-user': isAdmin }">
      <div class="user-avatar" @click="goToProfile">
        <img v-if="userProfilePicture" :src="userProfilePicture" alt="Foto de perfil">
        <div v-else class="default-avatar">{{ userInitials }}</div>
      </div>
      <div class="user-details" @click="goToProfile">
        <div class="user-name">{{ userName }}</div>
        <div class="user-email">{{ userEmail }}</div>
      </div>
      <button class="logout-button" @click="logout">
        <ion-icon name="log-out-outline"></ion-icon>
      </button>
    </div>
  </div>
</template>

<script>
import PublicQuestionChipComponent from './PublicQuestionChipComponent.vue';
import axios from 'axios';
import { COUNTRY } from '../utils/countryConfig';

export default {
  name: 'PublicQuestionsSidebarComponent',
  components: {
    'public-question-chip': PublicQuestionChipComponent
  },
  props: {
    selectedQuestionId: {
      type: String,
      default: null
    },
    initialQuestionId: {
      type: String,
      default: null
    },
    initialQuestion: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      publicQuestions: [],
      loading: false,
      offset: 0,
      limit: 10,
      hasMoreQuestions: true,
      sortBy: 'random',
      isMobile: false,
      userProfilePicture: '',
      userName: '',
      userEmail: '',
      permissions: []
    };
  },
  computed: {
    userInitials() {
      if (!this.userName) return '?';
      return this.userName.split(' ')
        .map(name => name.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    },
    isAdmin() {
      return this.permissions && this.permissions.includes('admin:data');
    }
  },
  mounted() {
    this.loadPublicQuestions(true);

    // Check if device is mobile
    this.checkMobile();

    // Add resize event listener
    window.addEventListener('resize', this.checkMobile);

    // Get user info from localStorage
    this.userProfilePicture = localStorage.getItem('user_profile_picture') || '';
    this.userName = localStorage.getItem('user_name') || '';
    this.userEmail = localStorage.getItem('user_email') || '';

    // Parse permissions from localStorage
    try {
      const permissionsData = localStorage.getItem('user_permissions');
      if (permissionsData) {
        this.permissions = JSON.parse(permissionsData);
      }
    } catch (error) {
      console.error('Error parsing permissions from localStorage:', error);
    }
  },

  beforeUnmount() {
    // Remove resize event listener
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    initialQuestion: {
      handler(newQuestion) {
        if (newQuestion && this.initialQuestionId) {
          console.log('initialQuestion changed:', newQuestion);
          // When initialQuestion becomes available, add it to the list if not already there
          this.addInitialQuestionToList(newQuestion);
        }
      },
      immediate: true
    }
  },
  methods: {
    goToMainView() {
      // Navigate to the main view
      this.$router.push({ name: "main-view" });
    },
    async loadPublicQuestions(reset = false) {
      if (this.loading) return;

      if (reset) {
        this.publicQuestions = [];
        this.offset = 0;
        this.hasMoreQuestions = true;
      }

      this.loading = true;

      try {
        const response = await axios.get(process.env.VUE_APP_BASE_URL + '/api/v2/public-questions/list/', {
          params: {
            offset: this.offset,
            limit: this.limit,
            sort_by: this.sortBy,
            country: COUNTRY
          }
        });

        let newQuestions = response.data;

        if (newQuestions.length < this.limit) {
          this.hasMoreQuestions = false;
        }

        // Handle the case where we're loading questions normally (not for an initial question)
        if (this.initialQuestionId && this.initialQuestion && reset) {
          // If we have an initial question and this is a reset, make sure it's not in the new questions
          newQuestions = newQuestions.filter(q => q._id !== this.initialQuestionId);

          // Check if the initial question is already in our list
          const hasInitialQuestion = this.publicQuestions.some(q => q._id === this.initialQuestionId);

          if (!hasInitialQuestion) {
            // Only add the initial question if it's not already there
            this.publicQuestions = [this.initialQuestion, ...newQuestions];
          } else {
            // Otherwise just append the new questions
            this.publicQuestions = [...this.publicQuestions, ...newQuestions];
          }
        } else {
          // Normal case - just append the new questions
          this.publicQuestions = [...this.publicQuestions, ...newQuestions];
        }

        this.offset += newQuestions.length;

        // Only auto-select the first question if there's no initialQuestionId
        if (newQuestions.length > 0 && !this.initialQuestionId) {
          if (reset || this.publicQuestions.length === newQuestions.length) {
            // Auto-select first question on reset or first load without initialQuestionId
            this.$emit('select-question', newQuestions[0]._id);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.error('No public questions found:', error);
        } else if (error.response && error.response.status === 401) {
          // emit logout event
          this.$emit('logout');
          this.$router.push({ name: 'main-view' });
          this.showToast("Sesión expirada o inválida - por favor, volvé a ingresar", "warning");
        } else if (error.response && error.response.status === 426) {
          this.showModal = true;
          this.showToast(error.response.data.detail, "error");
        } else if (error.response && (error.response.status === 400 || error.response.status === 429)) {
          const message = error.response.data.detail;
          this.showToast("ERROR: " + message, "error");
        } else if (error.response && (error.response.status === 409)) {
          const message = error.response.data.detail;
          this.showToast("ERROR: " + message, "error");
        } else {
          console.error('Error al obtener el historial de chat:', error);
          this.showToast("¡Estamos con mucha demanda! Intenta nuevamente.", "error");
        }
      } finally {
        this.loading = false;
      }
    },
    selectQuestion(event) {
      // If event is an object (like a PointerEvent), extract the questionId from it
      const questionId = typeof event === 'object' && event !== null ? event.target?.dataset?.id : event;

      // Only emit if we have a valid questionId
      if (questionId && typeof questionId === 'string') {
        this.$emit('select-question', questionId);

        // Close the sidebar on mobile when a question is selected
        if (window.innerWidth <= 768) {
          // Find the parent component that has isSidebarOpen
          let parent = this.$parent;
          while (parent && !('isSidebarOpen' in parent)) {
            parent = parent.$parent;
          }

          if (parent && 'isSidebarOpen' in parent) {
            parent.isSidebarOpen = false;
          }
        }
      } else {
        console.error('Invalid questionId:', event);
      }
    },
    handleScroll(event) {
      const element = event.target;
      if (
        !this.loading &&
        this.hasMoreQuestions &&
        element.scrollHeight - element.scrollTop <= element.clientHeight + 100
      ) {
        this.loadPublicQuestions();
      }
    },

    addInitialQuestionToList(question) {
      // Check if the question is already in the list
      const existingIndex = this.publicQuestions.findIndex(q => q._id === question._id);

      if (existingIndex !== -1) {
        // If it exists, just make sure it's selected
        this.$emit('select-question', question._id);
        return;
      }

      // If not in the list, add it at the beginning without reloading everything
      this.publicQuestions = [question, ...this.publicQuestions.filter(q => q._id !== question._id)];

      // Make sure it's selected
      this.$emit('select-question', question._id);
    },

    closeSidebar() {
      // Find the parent component that has isSidebarOpen
      let parent = this.$parent;
      while (parent && !('isSidebarOpen' in parent)) {
        parent = parent.$parent;
      }

      if (parent && 'isSidebarOpen' in parent) {
        parent.isSidebarOpen = false;
      } else {
        console.error('Could not find parent with isSidebarOpen property');
      }
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    goToProfile() {
      // Navigate to the profile page
      this.$router.push({ name: "perfil" });
    },

    logout() {
      // Make a post request to the logout endpoint first, then clear localStorage
      axios.post(process.env.VUE_APP_BASE_URL + '/api/auth/logout/')
        .then(() => {
          // Clear user data from localStorage only after successful logout
          localStorage.removeItem('user_email');
          localStorage.removeItem('user_name');
          localStorage.removeItem('user_permissions');
          localStorage.removeItem('user_profile_picture');
          localStorage.removeItem('user_subscription');

          console.log('Logged out');
          // Show toast message - can't use directly so we'll use an alert
          alert('Sesión cerrada. ¡Hasta la próxima!');

          // Redirect to login page
          this.$router.push('/login');
        })
        .catch((error) => {
          console.error('Error logging out:', error);
          alert('Error al intentar cerrar la sesión.');

          // Clear localStorage even on error for security
          localStorage.removeItem('user_email');
          localStorage.removeItem('user_name');
          localStorage.removeItem('user_permissions');
          localStorage.removeItem('user_profile_picture');
          localStorage.removeItem('user_subscription');

          // Still redirect to login page even if there's an error
          this.$router.push('/login');
        });
    }
  }
}
</script>

<style scoped>
.logo {
  max-width: 175px;
  margin-bottom: 36px;
  padding-top: 12px;
  cursor: pointer;
}

.public-questions-sidebar {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
  /* Fallback para navegadores antiguos */
  height: 100dvh;
  /* Dynamic viewport height */
  background-color: rgba(230, 230, 230, 0.9);
  width: 300px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

@media screen and (max-width: 768px) {
  .public-questions-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: 85vw;
    max-width: 90%;
    height: 100vh;
    /* Fallback para navegadores antiguos */
    height: 100dvh;
    /* Dynamic viewport height */
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    box-shadow: 5px 0 10px 1px rgba(0, 0, 0, 0.3);
    background-color: rgba(230, 230, 230, 1);
  }

  .public-questions-sidebar.open {
    transform: translateX(0) !important;
  }

  .sidebar-header {
    padding: 15px;
  }

  .logo {
    max-width: 150px;
    padding-top: 24px;
    margin-bottom: 24px;
  }

  .sort-selector select {
    width: 100%;
  }

  .sidebar-header h2 {
    color: #ffffff;
  }

  .loading-indicator,
  .end-message {
    color: #aaaaaa;
  }
}

.header-top {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.close-sidebar-button {
  background: none;
  border: none;
  color: #333333;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #333;
  position: absolute;
  top: 10px;
  right: 10px;
}

@media screen and (max-width: 768px) {
  .close-sidebar-button {
    color: #f0f0f0;
  }
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sidebar-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 12px 0;
}

.sort-selector {
  margin-top: 8px;
  align-self: stretch;
}

.sort-selector select {
  width: 100%;
  padding: 8px;
  background-color: #ffffff;
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  font-size: 14px;
}

@media screen and (max-width: 768px) {
  .sort-selector select {
    background-color: #333333;
    color: #ffffff;
    border: none;
  }
}

.questions-list {
  grid-row: 2;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
  /* Permite que el grid funcione correctamente */
  overscroll-behavior: contain;
}

.loading-indicator {
  text-align: center;
  padding: 16px;
  color: #666666;
  font-size: 14px;
}

.end-message {
  text-align: center;
  padding: 16px;
  color: #666666;
  font-size: 14px;
}

.user-info {
  grid-row: 3;
  width: 100%;
  padding: 15px;
  padding-bottom: calc(15px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  background-color: #a1344e;
  color: #ffffff;
}

.user-info.admin-user {
  background-color: #d4af37;
  /* Gold color for admins */
  color: #333;
  /* Darker text for better contrast on gold */
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  flex-shrink: 0;
  cursor: pointer;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-avatar {
  width: 100%;
  height: 100%;
  background-color: #a1344e;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.user-details {
  flex-grow: 1;
  overflow: hidden;
  cursor: pointer;
  color: #f0f0f0;
}

.user-info.admin-user .user-details {
  color: #333;
}

.user-name {
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-info.admin-user .user-email {
  color: #555;
}

.logout-button {
  background: none;
  border: none;
  color: #f0f0f0;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  padding: 5px;
  margin-left: 10px;
}

.logout-button:hover {
  color: #333;
}

.user-info.admin-user .logout-button {
  color: #333;
}

.user-info.admin-user .logout-button:hover {
  color: #000;
}

.platform-buttons {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-bottom: 12px;
}

.platform-button {
    border: 1px solid rgba(161, 52, 78, 0.3);
    background-color: transparent;
    color: #333333;
    border-radius: 4px;
    padding: 10px 14px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: all .2s ease-in-out;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: none;
}

.platform-button ion-icon {
    margin-right: 8px;
    font-size: 16px;
}

.whatsapp-button:hover {
    background-color: #25D366;
    color: white;
    border-color: #25D366;
}

.telegram-button:hover {
    background-color: #0088cc;
    color: white;
    border-color: #0088cc;
}

.platform-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}


@media screen and (max-width: 768px) {
  .user-info {
    padding: 10px;
  }
}


@media screen and (max-width: 480px) {
  .logo {
    max-width: 110px;
    padding-top: 24px;
    margin-bottom: 24px;
  }
}
</style>
